import { isLineCellDashed, isLineCellVisible, lineCellLabel, lookupOwners, type LineColumnDef } from '@project/contracts'
import { Button, Field, Input, Lookup, Modal, Money, Select } from '../design/index.js'
import type { LineRow } from '../app/api.js'
import { asDecimalString, asText } from './format.js'
import { cellEditable, formulaValue, optionsWithValue, previewAmount, type PickableRow } from './LineGrid.js'
import { slabOf, slabPatch, slabText, type GstSlab } from './tax-slabs.js'

/**
 * One line of a grid as a form (reference: "BOQ Detail" / "Material Info" —
 * Line #1 of 1, ‹ ›, Remove, Done ▴).
 *
 * The grid stays the fast way to key fifty lines; this is the way to read
 * and fill one long line — a BOQ narrative of three paragraphs, an estimate
 * whose fields depend on its cost type — without scrolling sideways. It
 * edits the same row through the same `patch`, so the two cannot disagree.
 *
 * Titled by the grid (`LineGridDef.detailTitle`), with "Line #n of N" under
 * it. Only the fields the row asks for are drawn — a column's `visibleWhen`
 * switches it on another cell — each with its label, its required mark and,
 * for a short fixed list, radio buttons (`control: 'radio'`). A field's label
 * may follow another cell (`labelsBy`: "Labour Rate" on a Labour estimate),
 * and one cell may be asked first (`asksFirst`: the Cost Type, which decides
 * what the rest of the line is). Worked-out amounts read beside the figures
 * they come from, without a box to type in.
 */
export const LineDetail = ({
  columns, lines, index, pickable, onPatch, onMove, onRemove, onClose, onAddNext, title, slabs = [], asksFirst, refColumn,
}: {
  columns: readonly LineColumnDef[]
  lines: readonly LineRow[]
  index: number
  pickable?: Readonly<Record<string, readonly PickableRow[]>>
  /** "BOQ Detail", "Material Info". Without one the dialog is titled by the line number. */
  title?: string
  /** The company's GST slabs, for a Tax field. */
  slabs?: readonly GstSlab[]
  /** Asked before anything else; until answered only it (and the reference) is drawn. */
  asksFirst?: string
  /** The row's automatic reference, drawn above the first question. */
  refColumn?: string
  onPatch: (index: number, values: Readonly<Record<string, string>>) => void
  onMove: (index: number) => void
  onRemove: (index: number) => void
  onClose: () => void
  /** Done, and straight on to a fresh line. */
  onAddNext: () => void
}) => {
  const row = lines[index]
  if (!row) return null
  const shown = columns.filter((c) => c.key !== 'seq' && isLineCellVisible(c, row) && !isLineCellDashed(c, row))
  const first = asksFirst ? shown.find((c) => c.key === asksFirst) : undefined
  const ref = first && refColumn ? shown.find((c) => c.key === refColumn) : undefined
  const fields = first
    ? [...(ref ? [ref] : []), first,
      ...(asText(row[first.key]) === '' ? [] : shown.filter((c) => c !== first && c !== ref))]
    : shown

  /** A worked-out amount: the line's total, or a product of two of its cells. */
  const computed = (c: LineColumnDef): string | null => {
    if (c.key === 'amount') return previewAmount(row)
    if (c.formula) return formulaValue(c.formula, row)
    return asDecimalString(row[c.key])
  }

  const control = (c: LineColumnDef) => {
    const open = cellEditable(c, row)
    const value = asText(row[c.key])
    if (c.type === 'money' && c.editable !== true) {
      const amount = computed(c)
      return (
        <div className="control" aria-readonly="true" style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', background: 'var(--surface-alt)' }}>
          {amount === null ? '—' : <Money value={amount} />}
        </div>
      )
    }
    if (c.taxSlab) {
      return open ? (
        <Select
          value={slabOf(slabs, row, c)?.id ?? ''}
          onChange={(v) => onPatch(index, slabPatch(slabs, c, v))}
          placeholder={slabText(slabs, row, c) || 'Not set'}
          options={slabs.map((s) => ({ value: s.id, label: s.label }))}
        />
      ) : <Input value={slabText(slabs, row, c)} onChange={() => {}} disabled />
    }
    if (!open) {
      const option = c.options?.find((o) => o.value === value)
      const shown = option?.label ?? (asText(row[`${c.key}Label`]) || (c.key === 'itemId' ? asText(row['itemLabel']) : value))
      return <Input value={shown} onChange={() => {}} disabled />
    }
    if (c.optionsFrom) {
      return <Select value={value} onChange={(v) => onPatch(index, { [c.key]: v })} placeholder="Not chosen" options={optionsWithValue(pickable?.[c.key] ?? [], value)} />
    }
    if (c.parentPicker) {
      return <Select value={value} onChange={(v) => onPatch(index, { [c.key]: v })} placeholder="Not chosen" options={[...(pickable?.[c.key] ?? [])]} />
    }
    if (c.options && c.control === 'radio') {
      return (
        <div role="radiogroup" aria-label={c.label} className="row" style={{ gap: 20, minHeight: 44 }}>
          {c.options.map((o) => (
            <label key={o.value} className="row" style={{ gap: 8, cursor: 'pointer' }}>
              <input
                type="radio"
                name={`line-${index}-${c.key}`}
                value={o.value}
                checked={value === o.value}
                onChange={() => onPatch(index, { [c.key]: o.value, ...(c.sets?.[o.value] ?? {}) })}
                style={{ width: 18, height: 18 }}
              />
              <span>{o.label}</span>
            </label>
          ))}
        </div>
      )
    }
    if (c.options) {
      return <Select value={value} onChange={(v) => onPatch(index, { [c.key]: v, ...(c.sets?.[v] ?? {}) })} placeholder="Not set" options={[...c.options]} />
    }
    if (c.key === 'itemId' || c.lookup) {
      const entity = (c.lookupBy ? c.lookupBy.entities[asText(row[c.lookupBy.column])] : undefined) ?? c.lookup?.entity ?? 'item'
      const labelKey = c.key === 'itemId' ? 'itemLabel' : `${c.key}Label`
      // Scoped by another cell of the row, and carrying what the pick says
      // onto it, as in the grid — a participant's contact among its
      // supplier's contacts, with that contact's email and phones (PUR-004).
      const owners = c.lookup?.scope
        ? lookupOwners({ lookup: { entity: c.lookup.entity, scope: c.lookup.scope } }, row)
        : undefined
      const carried = (option: { extra?: Readonly<Record<string, string | null>> } | null | undefined): Record<string, string> => {
        const next: Record<string, string> = {}
        if (!option) return next
        for (const [from, to] of Object.entries(c.lookup?.fills ?? {})) {
          const got = option.extra?.[from]
          if (c.lookup?.refill) next[to] = got ?? ''
          else if (got && !asText(row[to])) next[to] = got
        }
        return next
      }
      return (
        <Lookup
          key={`${entity}:${owners ?? ''}`}
          entity={entity}
          value={value}
          label={asText(row[labelKey]) || undefined}
          {...(owners ? { params: { owners } } : {})}
          {...(owners === null ? { emptyMessage: 'Choose the supplier first' } : {})}
          onChange={(v, option) => onPatch(index, {
            [c.key]: v,
            ...(option ? { [labelKey]: option.label } : {}),
            ...carried(option),
            ...(c.key === 'itemId' && option?.extra?.['unit'] && !asText(row['unit']) ? { unit: option.extra['unit'] } : {}),
            // Another item brings its own Tax (see LineGrid).
            ...(c.key === 'itemId' && option?.extra?.['taxPct'] ? { taxPct: option.extra['taxPct'] } : {}),
            ...(c.key === 'itemId' && asText(row['taxRateId']) !== '' ? { taxRateId: '' } : {}),
          })}
        />
      )
    }
    if (c.key === 'description') {
      return (
        <textarea
          className="control"
          style={{ minHeight: 120, resize: 'vertical' }}
          value={value}
          onChange={(e) => onPatch(index, { description: e.currentTarget.value })}
        />
      )
    }
    return (
      <Input
        value={value}
        type={c.type === 'date' ? 'date' : undefined}
        align={c.type === 'qty' || c.type === 'rate' || c.type === 'money' ? 'right' : 'left'}
        onChange={(v) => onPatch(index, { [c.key]: v })}
      />
    )
  }

  return (
    <Modal
      title={title ?? `Line #${index + 1} of ${lines.length}`}
      {...(title ? { subtitle: `Line #${index + 1} of ${lines.length}` } : {})}
      wide
      onClose={onClose}
      footerLeft={(
        <span className="row" style={{ gap: 6 }}>
          <Button kind="ghost" aria-label="Previous line" disabled={index === 0} onClick={() => onMove(index - 1)}>‹</Button>
          <Button kind="ghost" aria-label="Next line" disabled={index >= lines.length - 1} onClick={() => onMove(index + 1)}>›</Button>
        </span>
      )}
      actions={(
        <>
          <Button kind="ghost" onClick={() => onRemove(index)}>Remove</Button>
          <Button kind="ghost" onClick={onAddNext}>Done and add another</Button>
          <Button kind="primary" onClick={onClose}>Done</Button>
        </>
      )}
    >
      <div className="field-grid">
        {fields.map((c) => (
          <div key={c.key} style={c.key === 'description' || c.key === 'itemId' ? { gridColumn: '1 / -1' } : undefined}>
            <Field label={lineCellLabel(c, row)} hint={c.hint} required={c.required === true}>{control(c)}</Field>
          </div>
        ))}
      </div>
    </Modal>
  )
}
